"use client";

import { CURRENT_VERSION, type FunnelWizardState, type DraftSaveResult, type DraftLoadResult } from "@/lib/funnel-planner/schema";
import { migrateFunnelWizardState } from "@/lib/funnel-planner/migrations";
import { COOKIE_CONSENT_CHANGE_EVENT, COOKIE_CONSENT_STORAGE_KEY } from "@/lib/consent/state";
import { minimizeDraft, isFreshDraft, draftSession, mayLoadDraft, mayPersistDraft, type DraftSession } from "./draft-policy.mjs";

const DRAFT_KEY = "seoh_funnel_draft";
let session: DraftSession | null = null;

function syncConsent(): DraftSession {
  let granted = false;
  try {
    granted = typeof window !== "undefined" && window.__seohConsent?.has?.("preferences") === true;
  } catch { /* Missing or unavailable consent fails closed. */ }
  session = session === null
    ? draftSession(null, { type: "start", granted })
    : draftSession(session, { type: "consent", granted });
  if (!granted) clearDraft();
  return session;
}

/** Keep withdrawal effective even when the planner state does not change. */
export function watchDraftConsent(): () => void {
  if (typeof window === "undefined") return () => {};
  const onConsent = () => { syncConsent(); };
  const onStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === COOKIE_CONSENT_STORAGE_KEY) syncConsent();
  };
  syncConsent();
  window.addEventListener(COOKIE_CONSENT_CHANGE_EVENT, onConsent);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(COOKIE_CONSENT_CHANGE_EVENT, onConsent);
    window.removeEventListener("storage", onStorage);
  };
}

const DRAFT_EDIT_ACTIONS = new Set([
  "SET_TEMPLATE", "SET_AUDIENCE", "SET_GOAL", "SET_REGIONAL_SETTINGS",
  "SET_AUDIENCE_GOAL_DETAILS", "SET_CAPACITY_CONFIG", "SET_SCENARIOS", "SET_EXPERIMENTS",
  "SET_TRAFFIC_CHANNELS", "SET_PRODUCTS", "ADD_PRODUCT", "UPDATE_PRODUCT", "REMOVE_PRODUCT",
  "SET_STAGES", "SET_GRAPH", "SET_EXPENSES", "SET_PROJECTION_INPUT", "SET_PROJECTION_INPUTS",
]);

/**
 * Regrant requires a fresh planning edit; contact, restore and navigation do not qualify. A restore replaces the active
 * plan (Start over, a shared plan, a resumed draft or the quiz start), so it also drops the old plan's pending edit,
 * while navigation, contact entry and automatic syncs keep it for a retry of the same plan.
 */
export function noteDraftAction(type: string, source?: "automatic"): void {
  const current = syncConsent();
  session = type === "RESTORE"
    ? draftSession(current, { type: "discarded" })
    : draftSession(current, { type: "edit", nonContact: source !== "automatic" && DRAFT_EDIT_ACTIONS.has(type) });
}

/**
 * An explicit planning choice made outside the reducer, such as "Start from quiz result", is fresh planning input.
 * Loading or restoring a URL, share link or saved draft is not, and goes through noteDraftAction instead.
 */
export function notePlanningChoice(): void {
  session = draftSession(syncConsent(), { type: "edit", nonContact: true });
}

/** A mounting planner starts another plan, so a pending edit left by an earlier mount never authorizes a write. */
export function beginDraftPlan(): void {
  session = draftSession(syncConsent(), { type: "discarded" });
}

export function saveDraft(state: FunnelWizardState): DraftSaveResult {
  try {
    if (typeof window === "undefined") return "unavailable";
    // An untouched or merely resumed plan is not written; see mayPersistDraft.
    const current = syncConsent();
    if (!mayPersistDraft(current)) return "unavailable";
    const normalized = migrateFunnelWizardState({ ...state, contact: null }, CURRENT_VERSION);
    if (!normalized) return "unavailable";
    window.localStorage.setItem(DRAFT_KEY, JSON.stringify({
      updated_at: new Date().toISOString(), v: CURRENT_VERSION, state: minimizeDraft(normalized),
    }));
    // The write consumed the pending edit; a throwing write skips this and keeps the edit for the next autosave.
    session = draftSession(current, { type: "saved" });
    return "ok";
  } catch (err) {
    return err instanceof DOMException && err.name === "QuotaExceededError" ? "quota_exceeded" : "unavailable";
  }
}

export function loadDraft(): DraftLoadResult {
  try {
    if (typeof window === "undefined") return { state: null, discarded: false };
    if (!mayLoadDraft(syncConsent())) return { state: null, discarded: false };
    const raw = window.localStorage.getItem(DRAFT_KEY);
    if (!raw) return { state: null, discarded: false };
    const stored = JSON.parse(raw);
    if (!stored || typeof stored !== "object" || Array.isArray(stored)
      || !Number.isInteger(stored.v) || !isFreshDraft(stored.updated_at, Date.now())
      || !stored.state || typeof stored.state !== "object" || Array.isArray(stored.state)) {
      clearDraft();
      return { state: null, discarded: true };
    }
    const migrated = migrateFunnelWizardState({ ...stored.state, contact: null }, stored.v);
    if (!migrated) {
      clearDraft();
      return { state: null, discarded: true };
    }
    // Scrub legacy contact data even at the current schema version; keep the original expiry.
    const clean = JSON.stringify({ updated_at: stored.updated_at, v: CURRENT_VERSION, state: minimizeDraft(migrated) });
    if (clean !== raw) {
      try { window.localStorage.setItem(DRAFT_KEY, clean); } catch { clearDraft(); }
    }
    return { state: migrated, discarded: false };
  } catch {
    clearDraft();
    return { state: null, discarded: true };
  }
}

/** Removing the stored draft also drops a pending edit, so a discarded or submitted plan is never written back. */
export function clearDraft(): void {
  if (session !== null) session = draftSession(session, { type: "discarded" });
  try {
    if (typeof window !== "undefined") window.localStorage.removeItem(DRAFT_KEY);
  } catch { /* Storage can be unavailable in private or restricted contexts. */ }
}
